import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  FileTextIcon,
  PackageIcon,
  StoreIcon,
  UserIcon,
  WrenchIcon,
  XCircleIcon,
  ZapIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type { DisputeDetail, JobPreview, OrderPreview } from "../api/client";
import {
  useDisputeQuery,
  useUpdateDisputeStatusMutation,
} from "../api/queries";
import { DisputeDetailToolbar } from "../ui/DisputeDetailToolbar";

const STATUS_LABELS: Record<string, string> = {
  open: "ເປີດ",
  investigating: "ກຳລັງກວດສອບ",
  resolved: "ຕັດສິນແລ້ວ",
  rejected: "ປະຕິເສດ",
};

const TYPE_LABELS: Record<string, string> = {
  order: "ອໍເດີ",
  job: "ວຽກ",
};

function refShortId(referenceId: string | null): string {
  if (!referenceId) return "—";
  return `#${referenceId.slice(0, 8).toUpperCase()}`;
}

function totalAmountLabel(total: number, currency: string): string {
  const amount = formatPrice(total, currency, { appendCurrency: false });
  if (currency === "LAK") return `₭ ${amount}`;
  if (currency === "THB") return `฿ ${amount}`;
  if (currency === "USD") return `$ ${amount}`;
  return `${amount} ${currency}`;
}

function OrderInfoCard({ order }: { order: OrderPreview }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card className="flex gap-3 px-4">
        <div className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
            <UserIcon className="size-5 text-secondary" />
          </div>
          <div className="">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              ລູກຄ້າ
            </p>
            <p className="font-medium">{order.customerName ?? "—"}</p>
          </div>
        </div>

        <div className="min-w-0">
          {order.customerPhone && (
            <dl>
              <dt className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ຫົວຂໍ້
              </dt>
              <dd className="font-medium">{order.customerName ?? "—"}</dd>
            </dl>
          )}
        </div>
      </Card>

      <Card className="flex gap-3 px-4">
        <div className="flex gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
            <StoreIcon className="size-5 text-secondary" />
          </div>
          <div className="">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              ຮ້ານ
            </p>
            <p className="font-medium">{order.shopName ?? "—"}</p>
          </div>
        </div>
        <div className="min-w-0">
          {order.shopPhone && (
            <p className="text-muted-foreground text-sm">{order.shopPhone}</p>
          )}
          {(order.shopAddress || order.shopCity) && (
            <p className="mt-0.5 text-muted-foreground text-sm">
              {[order.shopAddress, order.shopCity].filter(Boolean).join(", ")}
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}

function OrderSummaryCard({ order }: { order: OrderPreview }) {
  return (
    <Card className="gap-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-medium text-muted-foreground uppercase tracking-wider">
          ລາຍການອໍເດີ
        </CardTitle>
        <span>
          ລາຄາລວມ:{" "}
          <span className="font-semibold text-primary">
            {totalAmountLabel(order.total, order.currency)}
          </span>
        </span>
      </CardHeader>
      <CardContent className="px-4">
        <div className="overflow-x-auto">
          <Table className="w-full text-sm">
            <TableHeader>
              <TableRow className="border-border/50 text-muted-foreground hover:bg-transparent">
                <TableHead className="pr-3 pb-2 font-medium uppercase tracking-wider">
                  ສິນຄ້າ
                </TableHead>
                <TableHead className="pr-3 pb-2 font-medium uppercase tracking-wider">
                  SKU
                </TableHead>
                <TableHead className="pr-3 pb-2 text-right font-medium uppercase tracking-wider">
                  ຈຳນວນ
                </TableHead>
                <TableHead className="pr-3 pb-2 text-right font-medium uppercase tracking-wider">
                  ລາຄາ
                </TableHead>
                <TableHead className="pb-2 text-right font-medium uppercase tracking-wider">
                  ລາຄາລວມ
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {order.items.map((item, i) => (
                <TableRow
                  key={`${order.orderId}-item-${i}-${item.productName ?? ""}`}
                  className="border-border/30 bg-muted/20 last:border-0 hover:bg-muted/25"
                >
                  <TableCell className="py-3 pr-3">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
                        <PackageIcon className="size-5 text-muted-foreground" />
                      </div>
                      <span className="truncate font-medium">
                        {item.productName ?? "—"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 pr-3 font-mono text-muted-foreground text-xs">
                    {item.sku ?? "—"}
                  </TableCell>
                  <TableCell className="py-3 pr-3 text-right">
                    {item.qty}
                  </TableCell>
                  <TableCell className="py-3 pr-3 text-right text-muted-foreground">
                    {totalAmountLabel(item.unitPrice, order.currency)}
                  </TableCell>
                  <TableCell className="py-3 text-right font-bold">
                    {totalAmountLabel(
                      item.unitPrice * item.qty,
                      order.currency,
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function JobInfoCard({
  job,
  openedByName,
}: {
  job: JobPreview;
  openedByName: string | null;
}) {
  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        ຫົວຂໍ້: {job.title}
        <span className="mx-2">·</span>
        <span className="font-mono">
          ວຽກ: {job.jobId.slice(0, 8).toUpperCase()}
        </span>
        <span className="mx-2">·</span>
        ສະຖານະ: {job.status}
        {openedByName && (
          <>
            <span className="mx-2">·</span>
            ເປີດໂດຍ: {openedByName}
          </>
        )}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="flex gap-3 px-4">
          <div className="flex gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
              <UserIcon className="size-5 text-secondary" />
            </div>
            <div className="">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ລູກຄ້າ
              </p>
              <p className="font-medium">{job.customerName ?? "—"}</p>
            </div>
          </div>
          <div className="min-w-0">
            {job.customerPhone && (
              <p className="text-muted-foreground text-sm">
                {job.customerPhone}
              </p>
            )}
            {(job.location || job.city) && (
              <p className="mt-0.5 text-muted-foreground text-sm">
                {[job.location, job.city].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </Card>

        <Card className="flex gap-3 px-4">
          <div className="flex gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary/10">
              <WrenchIcon className="size-5 text-secondary" />
            </div>
            <div className="">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                ຊ່າງແປງ
              </p>
              <p className="font-medium">
                {job.mechanicDisplayName ?? job.mechanicName ?? "—"}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function JobSummaryCard({ job }: { job: JobPreview }) {
  return (
    <Card className="gap-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-medium text-muted-foreground uppercase tracking-wider">
          ລາຍການວຽກ (ອາໄຫຼ່ + ຄ່າແຮງ)
        </CardTitle>
        <span>
          ລາຄາລວມ:{" "}
          <span className="font-semibold text-primary">
            {totalAmountLabel(job.total, job.currency)}
          </span>
        </span>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="w-full text-sm">
            <TableHeader>
              <TableRow className="border-border/50 text-muted-foreground hover:bg-transparent">
                <TableHead className="pr-3 pb-2 font-medium uppercase tracking-wider">
                  ສິນຄ້າ
                </TableHead>
                <TableHead className="pr-3 pb-2 font-medium uppercase tracking-wider">
                  SKU
                </TableHead>
                <TableHead className="pr-3 pb-2 text-right font-medium uppercase tracking-wider">
                  ຈຳນວນ
                </TableHead>
                <TableHead className="pr-3 pb-2 text-right font-medium uppercase tracking-wider">
                  ລາຄາ
                </TableHead>
                <TableHead className="pb-2 text-right font-medium uppercase tracking-wider">
                  ລາຄາລວມ
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {job.items.map((item, i) => (
                <TableRow
                  key={`${job.jobId}-item-${i}-${item.productName ?? ""}`}
                  className="border-border/30 bg-muted/20 last:border-0 hover:bg-muted/25"
                >
                  <TableCell className="py-3 pr-3">
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
                        <PackageIcon className="size-5 text-muted-foreground" />
                      </div>
                      <span className="truncate font-medium">
                        {item.productName ?? "ອາໄຫຼ່"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 pr-3 font-mono text-muted-foreground text-xs">
                    {item.sku ?? "—"}
                  </TableCell>
                  <TableCell className="py-3 pr-3 text-right">
                    {item.qty}
                  </TableCell>
                  <TableCell className="py-3 pr-3 text-right text-muted-foreground">
                    {totalAmountLabel(item.unitPrice, job.currency)}
                  </TableCell>
                  <TableCell className="py-3 text-right font-bold">
                    {totalAmountLabel(item.unitPrice * item.qty, job.currency)}
                  </TableCell>
                </TableRow>
              ))}
              {job.laborFee > 0 && (
                <TableRow className="border-border/30 bg-muted/20 last:border-0 hover:bg-muted/25">
                  <TableCell className="py-3 pr-3" colSpan={2}>
                    <div className="flex items-center gap-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
                        <WrenchIcon className="size-5 text-muted-foreground" />
                      </div>
                      <span className="font-medium">ຄ່າແຮງບໍລິການ</span>
                    </div>
                  </TableCell>
                  <TableCell className="py-3 pr-3 text-right">—</TableCell>
                  <TableCell className="py-3 pr-3 text-right text-muted-foreground">
                    {totalAmountLabel(job.laborFee, job.currency)}
                  </TableCell>
                  <TableCell className="py-3 text-right font-bold">
                    {totalAmountLabel(job.laborFee, job.currency)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function InternalActivityCard({ dispute }: { dispute: DisputeDetail }) {
  const statusLabel = STATUS_LABELS[dispute.status] ?? dispute.status;
  const events: { title: string; time: string; detail?: string }[] = [
    {
      title: "ເປີດຂໍ້ພິພາດ",
      time: `${formatDateTimeLocal(dispute.createdAt)} • ${dispute.openedByName ?? dispute.openedBy}`,
    },
  ];
  if (dispute.status !== "open") {
    events.push({
      title: "ອັບເດດສະຖານະ",
      time: formatDateTimeLocal(dispute.createdAt),
      detail: `ສະຖານະເປັນ «${statusLabel}»`,
    });
  }
  if (dispute.resolvedAt) {
    events.push({
      title: dispute.status === "resolved" ? "ຕັດສິນແລ້ວ" : "ປະຕິເສດ",
      time: formatDateTimeLocal(dispute.resolvedAt),
      detail: dispute.resolution
        ? dispute.resolution.slice(0, 60) +
          (dispute.resolution.length > 60 ? "…" : "")
        : undefined,
    });
  }

  return (
    <Card className="gap-0">
      <CardHeader>
        <CardTitle className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
          ກິດຈະກຳພາຍໃນ
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-4 border-primary/30 border-l-2 pl-4">
          {events.map((e, i) => (
            <li key={`${e.title}-${e.time}-${i}`} className="relative pl-0">
              <p className="font-medium text-sm">{e.title}</p>
              <p className="text-muted-foreground text-xs">{e.time}</p>
              {e.detail && (
                <p className="mt-0.5 rounded-md bg-muted/50 px-2 py-1 text-muted-foreground text-xs">
                  {e.detail}
                </p>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function DisputeDetailContent({ dispute }: { dispute: DisputeDetail }) {
  const nav = useNavigate({ from: "/app/disputes/$id" });
  const { id } = useParams({ from: "/app/disputes/$id" });
  const updateStatus = useUpdateDisputeStatusMutation(id);
  const [resolution, setResolution] = useState("");

  const canResolve = dispute.status === "investigating";
  const statusLabel = STATUS_LABELS[dispute.status] ?? dispute.status;
  const typeLabel = TYPE_LABELS[dispute.type] ?? dispute.type;

  const handleResolve = async () => {
    const text = resolution.trim();
    if (!text) return;
    await updateStatus.mutateAsync({ action: "resolved", resolution: text });
    nav({ to: "/app/disputes" });
  };

  const handleReject = async () => {
    const text = resolution.trim();
    if (!text) return;
    await updateStatus.mutateAsync({ action: "rejected", resolution: text });
    nav({ to: "/app/disputes" });
  };

  return (
    <>
      <Header>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => nav({ to: "/app/disputes" })}
            aria-label="ກັບຄືນ"
          >
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: "ຂໍ້ພິພາດ", to: "/app/disputes" },
              { label: "ລາຍລະອຽດ" },
            ]}
          />
        </div>
      </Header>

      <Main>
        <DisputeDetailToolbar
          disputeId={dispute.id}
          status={dispute.status}
          statusLabel={statusLabel}
        />

        {/* แถวไฮไลต์: ເຫດຜົນ + ສະຖານະ (ຊ້າຍ) */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="gap-0 rounded-xl border-border bg-card p-4">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              ເຫດຜົນຂໍ້ພິພາດ
            </p>
            <div className="mt-2 flex items-center gap-2">
              <AlertTriangleIcon className="size-5 shrink-0 text-primary" />
              <p className="font-bold text-lg">{dispute.reason ?? "—"}</p>
            </div>
          </Card>
          <Card className="gap-0 rounded-xl border-border bg-card p-4">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              ສະຖານະ
            </p>
            <div className="mt-2 flex items-center gap-2">
              <span
                className="size-2.5 shrink-0 rounded-full bg-primary"
                aria-hidden
              />
              <p className="font-bold text-lg">{statusLabel}</p>
            </div>
          </Card>
          <Card className="gap-0 rounded-xl border-border bg-card p-4">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
              {typeLabel === "ອໍເດີ" ? "ອ້າງອີງອໍເດີ" : "ອ້າງອີງວຽກ"}
            </p>
            <p className="mt-1 font-bold font-mono text-secondary underline underline-offset-2">
              {dispute.referenceId ? refShortId(dispute.referenceId) : "—"}
            </p>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          {/* คอลัมน์ซ้าย: ຂໍ້ມູນອໍເດີ/ວຽກ + ສະຫຼຸບລາຍການ */}
          <div className="space-y-6">
            {dispute.orderPreview ? (
              <>
                <OrderInfoCard order={dispute.orderPreview} />
                <OrderSummaryCard order={dispute.orderPreview} />
              </>
            ) : dispute.jobPreview ? (
              <>
                <JobInfoCard
                  job={dispute.jobPreview}
                  openedByName={dispute.openedByName}
                />
                <JobSummaryCard job={dispute.jobPreview} />
              </>
            ) : (
              <Card className="gap-0">
                <CardHeader>
                  <CardTitle className="font-medium text-muted-foreground text-xs uppercase tracking-wider">
                    ຂໍ້ມູນວຽກ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">
                    ອ້າງອີງ:{" "}
                    {dispute.referenceId
                      ? refShortId(dispute.referenceId)
                      : "—"}
                  </p>
                  <p className="mt-1 text-muted-foreground text-sm">
                    ເປີດໂດຍ: {dispute.openedByName ?? dispute.openedBy}
                  </p>
                </CardContent>
              </Card>
            )}
            {dispute.resolution && (
              <Card className="gap-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-medium text-base">
                    <FileTextIcon className="size-4 text-secondary" />
                    ການຕັດສິນ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{dispute.resolution}</p>
                  {dispute.resolvedAt && (
                    <p className="mt-2 text-muted-foreground text-xs">
                      ເມື່ອ {formatDateTimeLocal(dispute.resolvedAt)}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* คอลัมน์ขวา: ອ້າງອີງ, ການຕັດສິນ, ກິດຈະກຳພາຍໃນ */}
          <div className="space-y-6">
            <RequirePermissions all={["disputes:update"]}>
              <Card className="gap-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-medium text-base">
                    <ZapIcon className="size-4 text-primary" />
                    ການຕັດສິນຂໍ້ພິພາດ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label
                      htmlFor="dispute-resolution-input"
                      className="mb-1 block font-medium text-muted-foreground text-xs uppercase tracking-wider"
                    >
                      ເຫດຜົນການຕັດສິນ / ປະຕິເສດ *
                    </label>
                    <Textarea
                      id="dispute-resolution-input"
                      placeholder="ກະລຸນາບັນທຶກການຕັດສິນແລະເຫດຜົນໃຫ້ຊັດເຈນ"
                      value={resolution}
                      onChange={(e) => setResolution(e.target.value)}
                      rows={4}
                      className="resize-none"
                      disabled={!canResolve}
                    />
                    <p className="mt-1 text-muted-foreground text-xs">
                      ການຕັດສິນຈະຖືກບັນທຶກໃນ audit log
                    </p>
                  </div>
                  {canResolve && (
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={handleResolve}
                        disabled={!resolution.trim() || updateStatus.isPending}
                      >
                        <CheckCircleIcon className="mr-2 size-4" />
                        ຕັດສິນແລ້ວ
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleReject}
                        disabled={!resolution.trim() || updateStatus.isPending}
                      >
                        <XCircleIcon className="mr-2 size-4" />
                        ປະຕິເສດຂໍ້ພິພາດ
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </RequirePermissions>

            <InternalActivityCard dispute={dispute} />
          </div>
        </div>
      </Main>
    </>
  );
}

export function DisputeDetailPage() {
  const { id } = useParams({ from: "/app/disputes/$id" });
  const { data, isLoading, error } = useDisputeQuery(id);

  if (isLoading) {
    return (
      <>
        <Header />
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Header />
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບຂໍ້ພິພາດ ຫຼື ເກີດຂໍ້ຜິດພາດ
          </div>
        </Main>
      </>
    );
  }

  return <DisputeDetailContent dispute={data} />;
}
